import type { TProfileResponse } from '@/api/auth/types'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { FilterUserValues } from '../validation'
import type { MutationProvinceValues } from './validation'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['admin_provinsi', key] as const

const getAll = (
  params?: FilterUserValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<TProfileResponse>>> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<TProfileResponse>>
      >('/user/admin_provinsi', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<TProfileResponse>> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<TProfileResponse>>(
        `/user/admin_provinsi/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<TProfileResponse>,
  MutationProvinceValues
> => ({
  mutationFn: async (data: MutationProvinceValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<TProfileResponse>>(
        '/user/admin_provinsi',
        {
          ...data,
          permissions: data.permissions?.map(permission => permission.id),
          provinces: data.provinces?.map(province => province.value)
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<TProfileResponse>,
  MutationProvinceValues
> => ({
  mutationFn: async (data: MutationProvinceValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<TProfileResponse>>(
        `/user/admin_provinsi/${data.id_user}`,
        {
          ...data,
          permissions: data.permissions?.map(permission => permission.id),
          provinces: data.provinces?.map(province => province.value)
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<TProfileResponse>,
  { id: string }
> => ({
  mutationFn: async (data: { id: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<TProfileResponse>
      >(`/user/admin_provinsi/${data.id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const adminProvince = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData
}
